import axios from 'axios';
import { getAuth } from 'firebase/auth';
import env from '../config/env';

// Create an axios instance with default config
const api = axios.create({
  baseURL: env.API_URL,  // Using environment-specific configuration
  headers: {
    'Content-Type': 'application/json',
  },
  // Enable credentials to ensure cookies are sent with requests
  withCredentials: true,
});

// Export the base URL for other services to use
export const API_BASE_URL = env.API_URL;

// Add a request interceptor to add the auth token to requests
api.interceptors.request.use(
  async (config) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (user) {
        // Get a fresh token each time
        const token = await user.getIdToken(true);
        config.headers.Authorization = `Bearer ${token}`;
        console.log('Added authentication token to request');
      } else {
        console.warn('No authenticated user found for request to:', config.url);
      }
      
      return config;
    } catch (error) {
      console.error('Error in request interceptor:', error);
      return Promise.reject(error);
    }
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    // Log successful responses for debugging
    console.log(`API Response [${response.status}]:`, response.config.url);
    return response;
  },
  (error) => {
    // Handle specific error cases with detailed logging
    if (error.response) {
      // Server responded with an error status code
      const { status, data } = error.response;
      const url = error.config.url;
      
      console.error(`API Error [${status}] for ${url}:`, data);
      
      // Check for CORS errors
      if (error.message && error.message.includes('Network Error')) {
        console.error('Possible CORS issue detected');
      }
      
      if (status === 401) {
        console.error('Authentication failed. Token may be invalid or expired.');
        // Only redirect to login if not already on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
      
      if (status === 403) {
        console.error('Permission denied. User does not have access to this resource.');
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('Network error, no response received:', error.request);
      console.error('This might be a CORS issue or the server is not running.');
    } else {
      // Something else happened while setting up the request
      console.error('Error setting up request:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// API endpoints
const endpoints = {
  // Authentication
  login: (email, password) => api.post('/api/auth/login', { email, password }),
  register: (userData) => api.post('/api/auth/register', userData),
  
  // Quotes
  getQuotes: (customerId) => api.get(`/api/quotes/user/${customerId}`),
  getQuote: (id) => api.get(`/api/quotes/${id}`),
  createQuote: (quoteData) => api.post('/api/quotes', quoteData),
  acceptQuote: (id) => api.put(`/api/quotes/${id}/accept`),
  rejectQuote: (id) => api.put(`/api/quotes/${id}/reject`),
  
  // Provider-specific quotes
  getProviderQuotes: (providerId) => api.get(`/api/providers/${providerId}/quotes`),
  getProviderQuotesByStatus: (providerId, status) => api.get(`/api/providers/${providerId}/quotes?status=${status}`),
  
  // Appointments
  getAppointments: (firebaseUserId) => api.get(`/api/appointments/user/${firebaseUserId}`),
  getProviderAppointments: (providerId) => api.get(`/api/appointments/provider/${providerId}`),
  getAppointment: (id) => api.get(`/api/appointments/${id}`),
  createAppointment: (appointmentData) => api.post('/api/appointments', appointmentData),
  rescheduleAppointment: (rescheduleData) => api.post('/api/appointments/reschedule', rescheduleData),
  cancelAppointment: (cancelData) => api.post('/api/appointments/cancel', cancelData),
  completeAppointment: (id, notes) => api.post(`/api/appointments/complete/${id}`, { notes }),
  
  // Calendar
  getCalendarEvents: () => api.get('/api/calendar/events'),
  downloadICS: (appointmentId) => api.get(`/api/calendar/ics/${appointmentId}`),
  sendCalendarInvite: (appointmentId) => api.post(`/api/calendar/send-invite/${appointmentId}`),
  
  // User profile
  getUserProfile: () => api.get('/api/user/profile'),
  updateUserProfile: (profileData) => api.put('/api/user/profile', profileData),
  
  // Service providers
  getServiceProviders: () => api.get('/api/service-providers'),
  getServiceProvider: (id) => api.get(`/api/service-providers/${id}`),
};

export default {
  ...endpoints,
  instance: api,
};
